import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamService } from '../services';
import { OwnerListItem, TeamRequest, TeamInvitation, TeamStatus } from '../types';
import { useAuthStore } from '../store';
import { ASSET_TYPE_ICONS } from '../components/widgets/widgetIcons';

const JoinTeam: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [owners, setOwners] = useState<OwnerListItem[]>([]);
  const [sentRequests, setSentRequests] = useState<TeamRequest[]>([]);
  const [teamStatus, setTeamStatus] = useState<TeamStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [ownersData, requestsData, statusData, invitationsData] = await Promise.all([
        teamService.getAvailableOwners(),
        teamService.getSentRequests(),
        teamService.getTeamStatus(),
        teamService.getReceivedInvitations(),
      ]);
      setOwners(ownersData);
      setSentRequests(requestsData);
      setTeamStatus(statusData);
      setInvitations(invitationsData);

      // If user already has a team, redirect to dashboard
      if (statusData.hasTeam) {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestToJoin = async (ownerId: string) => {
    try {
      setSubmitting(true);
      setError(null);
      await teamService.createRequest({
        ownerId,
        message: message || undefined,
      });
      setMessage('');
      setSelectedOwner(null);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespondToInvitation = async (invitationId: string, accepted: boolean) => {
    try {
      setRespondingId(invitationId);
      setError(null);
      await teamService.respondToInvitation(invitationId, { accepted });
      if (accepted) {
        navigate('/dashboard');
      } else {
        await loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to respond to invitation');
    } finally {
      setRespondingId(null);
    }
  };

  const getPendingRequestForOwner = (ownerId: string) => {
    return sentRequests.find(r => r.ownerId === ownerId && r.status === 'pending');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning-100 text-warning-700';
      case 'approved':
        return 'bg-success-100 text-success-700';
      case 'rejected':
        return 'bg-danger-100 text-danger-700';
      default:
        return 'bg-neutral-100 text-neutral-700';
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-neutral-50">
      {/* Header */}
      <div className="bg-surface border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-neutral-900">Join a Team</h1>
          <p className="mt-2 text-neutral-600">
            Welcome, {user?.fullName}! To start working on tasks, you need to join a team. Select a
            manager below and request to join their team.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Team Invitations Section */}
        {invitations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Team Invitations</h2>
            <p className="text-sm text-neutral-500 mb-4">
              These teams have invited you to join.
            </p>
            <div className="bg-surface rounded-xl shadow-sm border border-neutral-100 divide-y">
              {invitations.map(invitation => (
                <div key={invitation.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-success-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-semibold text-success-600">
                        {invitation.ownerName?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{invitation.ownerName}</p>
                      <p className="text-sm text-neutral-500">{invitation.ownerEmail}</p>
                      {invitation.message && (
                        <p className="text-sm text-neutral-600 mt-1 italic">"{invitation.message}"</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRespondToInvitation(invitation.id, true)}
                      disabled={respondingId === invitation.id}
                      className="px-4 py-2 bg-success-600 text-white rounded-lg text-sm font-medium hover:bg-success-700 disabled:opacity-50"
                    >
                      {respondingId === invitation.id ? 'Joining...' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleRespondToInvitation(invitation.id, false)}
                      disabled={respondingId === invitation.id}
                      className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Requests Section */}
        {sentRequests.filter(r => r.status === 'pending').length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Pending Requests</h2>
            <div className="bg-surface rounded-xl shadow-sm border border-neutral-100 divide-y">
              {sentRequests
                .filter(r => r.status === 'pending')
                .map(request => (
                  <div key={request.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-neutral-900">{request.ownerName}</p>
                      <p className="text-sm text-neutral-500">{request.ownerEmail}</p>
                      {request.message && (
                        <p className="text-sm text-neutral-600 mt-1 italic">"{request.message}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(request.status)}`}
                      >
                        Pending
                      </span>
                      <span className="text-sm text-neutral-500">
                        Sent {new Date(request.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Request History */}
        {sentRequests.filter(r => r.status !== 'pending').length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Request History</h2>
            <div className="bg-surface rounded-xl shadow-sm border border-neutral-100 divide-y">
              {sentRequests
                .filter(r => r.status !== 'pending')
                .map(request => (
                  <div key={request.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-neutral-900">{request.ownerName}</p>
                      <p className="text-sm text-neutral-500">{request.ownerEmail}</p>
                      {request.responseMessage && (
                        <p className="text-sm text-neutral-600 mt-1">
                          Response: "{request.responseMessage}"
                        </p>
                      )}
                    </div>
                    <span
                      className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadge(request.status)}`}
                    >
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Available Teams */}
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Available Teams</h2>
          {owners.length === 0 ? (
            <div className="bg-surface rounded-xl shadow-sm border border-neutral-100 p-8 text-center">
              <div className="text-5xl mb-4">{ASSET_TYPE_ICONS.company}</div>
              <p className="text-neutral-600">No teams available to join at this time.</p>
              <p className="text-sm text-neutral-500 mt-2">
                Please check back later or contact your administrator.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {owners.map(owner => {
                const pendingRequest = getPendingRequestForOwner(owner.id);
                const isSelected = selectedOwner === owner.id;

                return (
                  <div
                    key={owner.id}
                    className="bg-surface rounded-xl shadow-sm border border-neutral-100 overflow-hidden"
                  >
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-xl font-semibold text-primary-600">
                            {owner.fullName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{owner.fullName}</p>
                          <p className="text-sm text-neutral-500">{owner.email}</p>
                        </div>
                      </div>
                      <div>
                        {pendingRequest ? (
                          <span className="px-4 py-2 bg-warning-100 text-warning-700 rounded-lg text-sm font-medium">
                            Request Pending
                          </span>
                        ) : isSelected ? (
                          <button
                            onClick={() => setSelectedOwner(null)}
                            className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium hover:bg-neutral-200"
                          >
                            Cancel
                          </button>
                        ) : (
                          <button
                            onClick={() => setSelectedOwner(owner.id)}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                          >
                            Request to Join
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Request form */}
                    {isSelected && !pendingRequest && (
                      <div className="px-4 pb-4 border-t border-neutral-100 pt-4 bg-neutral-50">
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Add a message (optional)
                        </label>
                        <textarea
                          value={message}
                          onChange={e => setMessage(e.target.value)}
                          placeholder="Introduce yourself or explain why you want to join this team..."
                          rows={3}
                          maxLength={500}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg resize-none"
                        />
                        <div className="flex justify-between items-center mt-3">
                          <span className="text-sm text-neutral-500">
                            {message.length}/500 characters
                          </span>
                          <button
                            onClick={() => handleRequestToJoin(owner.id)}
                            disabled={submitting}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                          >
                            {submitting ? 'Sending...' : 'Send Request'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinTeam;
